import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserEditComponent } from '../users/user-edit/user-edit.component';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { NgxSpinnerService } from 'ngx-spinner';
import { UsersService } from '../users/users.service';
import { SuccessComponent } from 'src/app/shared/modals/success/success.component';
import { SharedService } from '../../../../../../shared/shared.service';
import { OrganizationService } from 'src/app/modules/obsolete/components/organization/organization.service';
import { CreateOrganisationHierarchyService } from '../create-organisation-hierarchy/create-organisation-hierarchy.service';
import { AlertComponent } from 'src/app/shared/modals/alert/alert.component';
import { firstValueFrom } from 'rxjs';


@Component({
	selector: 'app-view-level-users',
	templateUrl: './view-level-users.component.html',
	styleUrls: ['./view-level-users.component.scss']
})
export class ViewLevelUsersComponent implements OnInit {
	selectManagerOption: any = false;
	filteredUsers: any = [];
	levelName: string = '';
	organisationId: string = '';
	usersDetailsDataSource = new MatTableDataSource<any>([]);
	managersListSource = new MatTableDataSource<any>([]);
	@ViewChild(MatPaginator) paginator!: MatPaginator;
	displayedTableColumns: string[] = ['status', 'name', 'role', 'email', 'edit', 'delete'];
	mgrsTableColumns: string[] = ['status', 'name', 'role', 'email', 'edit', 'remove'];
	levels: any;
	blockLoader: string = 'blockLoader';
	organisationHierarchy: any;
	levelManagers: any = [];
	managersCopy: any = [];
	levelIndex: number = 0;
	globalIndex: number = 0;
	// orgAllUsers: any;
	modalTitle: string = "Show Users In Location";
	location: string = "";
	searchForManager: string = "";
	roleSelectionManager: string = "all";
	rolesData: any;
	orgRolesNames: any = [{
		id: "all",
		name: "All"
	}];
	currentLevel: any;
	managersList: any = [];
	orgCode: string = '';
	users: any = [];
	lastUserDoc: any;
	hasPreviousUsers: boolean = false;
	hasMoreUsers: boolean = true;
	currentIndex = [0, 0];
	isSearchingUser = false;
	tempManagers: any = [];

	constructor(
		private dialog: MatDialog,
		private snackbar: MatSnackBar,
		@Inject(MAT_DIALOG_DATA) private data: any,
		private spinner: NgxSpinnerService,
		private usersService: UsersService,
		private sharedService: SharedService,
		public dialogRef: MatDialogRef<ViewLevelUsersComponent>,
		private organizationService: OrganizationService,
		private createOrganisationHierarchyService: CreateOrganisationHierarchyService
	) {}

	async ngOnInit() {
		if (this.data) {
			await this.initializeVariables();
		}
	}

	async getRoleName(roleId: string) {
		return await this.usersService.getRoleName(roleId);
	}

	ngAfterViewInit() {
		this.usersDetailsDataSource.paginator = this.paginator;
	}

	closeDialog() {
		this.dialogRef.close();
	}

	addUser(role: string) {
		const addUserDialog = this.dialog.open(UserEditComponent, {
			maxWidth: '80%',
			minWidth: 'fit-content',
			data: {
				editLevels: this.levels,
				organisationId: this.organisationId,
				role: role,
				orgCode: this.orgCode
			}
		});

		addUserDialog.afterClosed().subscribe(async result => {
			if (result?.status === 'success') {
				this.snackbar.open("User added successfully!", "OK", {
					duration: 3000
				});

				await this.updateFilteredUsers(false, true);
			}
		});
	}

	editUser(userData?: string) {
		const dialogRef = this.dialog.open(UserEditComponent, {
			maxWidth: '80%',
			minWidth: '80%',
			data: {
				userData,
				organisationId: this.organisationId,
				orgCode: this.orgCode
			}
		});

		dialogRef.afterClosed().subscribe(async result => {
			if (result?.status === 'success') {
				let msg = '';
				if (userData) {
					msg = 'User edited successfully!';
				} else {
					msg = 'User added successfully!';
				}
				this.snackbar.open(msg, "OK", {
					duration: 1000
				});
				await this.updateFilteredUsers(false);
			}
		});
	}

	async deleteUser(element: any, index: number) {
		// console.log("deleteSubject", element.id);
		if (!element || !element.id) {
			this.dialog.open(SuccessComponent, {
				data: { title: "Failed", subTitle: "Could not identify that user. Please refresh and try again." }
			});
			return;
		}

		// Deleting a user is permanent and also wipes their progress, so always confirm first.
		let subTitle = `Are you sure you want to delete user: ${element.name}?`
			+ `<br><br>This permanently deletes their account and all of their learning progress. This cannot be undone.`;

		const confirmRef = this.dialog.open(AlertComponent, {
			data: {
				title: "Are you sure?",
				subTitle
			}
		});

		const confirmed = await firstValueFrom(confirmRef.afterClosed());
		if (!confirmed) {
			return;
		}

		await this.spinner.show("blockLoader");
		const res: any = await this.usersService.deleteUser(element.id);

		if (!res || res.status !== 'success') {
			await this.spinner.hide("blockLoader");
			this.dialog.open(SuccessComponent, {
				data: { title: "Failed", subTitle: "Unable to delete the user. Please try again." }
			});
			return;
		}

		// A deleted user must not stay behind as a manager anywhere in the hierarchy,
		// otherwise the org doc keeps an orphaned id that still grants analytics scope.
		await this.removeUserFromAllManagerLists(element.id);
		await this.spinner.hide("blockLoader");

		this.dialog.open(SuccessComponent, { data: { title: "Deleted!", subTitle: "User deleted successfully." } });
		await this.updateFilteredUsers(false);
	}

	/**
	 * Strips a user id out of every `managers` array in the hierarchy and persists it.
	 * Returns silently when the user was not a manager anywhere - nothing to write.
	 */
	async removeUserFromAllManagerLists(userId: string) {
		let changed = false;

		for (let level of this.organisationHierarchy) {
			for (let val of level.values) {
				if (Array.isArray(val.managers) && val.managers.includes(userId)) {
					val.managers = val.managers.filter((id: any) => id !== userId);
					changed = true;
				}
			}
		}

		if (!changed) {
			return;
		}

		const res: any = await this.organizationService.addManagers(this.organisationId, this.organisationHierarchy);
		if (!res || res.status !== 'success') {
			this.snackbar.open("User was deleted, but their manager access could not be cleared. Please retry.", "OK", {
				duration: 6000
			});
			return;
		}

		// Copy, never alias - `addToManagersList` mutates `levelManagers` in place and
		// a shared reference would silently edit the hierarchy before anything is saved.
		this.levelManagers = [...(this.currentLevel?.managers ?? [])];
		this.managersCopy = [...this.levelManagers];
		this.refreshManagersTable();
	}

	/**
	 * Removes a manager from THIS location only. This is deliberately not `deleteUser` -
	 * un-assigning a manager must never delete their account or their learning progress.
	 */
	async removeManager(element: any, index: number) {
		const userId = element?.id;

		if (!userId) {
			this.dialog.open(SuccessComponent, {
				data: { title: "Failed", subTitle: "Could not identify that manager. Please refresh and try again." }
			});
			return;
		}

		const updatedManagers = this.levelManagers.filter((id: any) => id !== userId);

		await this.spinner.show("blockLoader");
		this.currentLevel.managers = updatedManagers;
		const res: any = await this.organizationService.addManagers(this.organisationId, this.organisationHierarchy);
		await this.spinner.hide("blockLoader");

		if (!res || res.status !== 'success') {
			// Roll the in-memory change back so the UI keeps matching the database.
			this.currentLevel.managers = [...this.levelManagers];
			this.dialog.open(SuccessComponent, {
				data: { title: "Failed", subTitle: "Unable to remove the manager. Please try again." }
			});
			return;
		}

		this.levelManagers = updatedManagers;
		this.managersCopy = [...this.levelManagers];
		this.refreshManagersTable();

		this.snackbar.open(`${element.name || 'Manager'} removed from ${this.location}`, "OK", {
			duration: 3000
		});
	}

	/** Re-renders the managers table from the current `levelManagers` ids. */
	refreshManagersTable() {
		this.managersList = this.managersList.filter((mgr: any) => {
			return mgr && this.levelManagers.includes(mgr.id);
		});

		// Keep the parent hierarchy tooltip in sync with what we just saved.
		if (this.data?.hierarchyOption) {
			this.data.hierarchyOption.managerNames = [...this.managersList];
		}

		this.managersListSource = new MatTableDataSource<any>(this.managersList);
	}

	selectManager() {
		this.selectManagerOption = true;
		if (this.selectManagerOption) {
			this.displayedTableColumns = ['select', 'name', 'role', 'email', 'location'];
		}

		this.usersDetailsDataSource = new MatTableDataSource<any>(this.users);
		this.usersDetailsDataSource.paginator = this.paginator;
		this.modalTitle= "Search & Select Manager";
		// console.log('selectManagerOption', this.selectManagerOption)
		// const sortedAllUsers = this.orgAllUsers.sort((userA: any, userB: any) => {
		// 	return userA.name < userB.name ? - 1 : 1;
		// });
	}

	cancel() {
		this.selectManagerOption = false;
		this.displayedTableColumns = ['status', 'name', 'role', 'email', 'edit', 'delete'];
		this.levelManagers = [...this.managersCopy];
		this.usersDetailsDataSource = new MatTableDataSource<any>(this.filteredUsers);
		this.usersDetailsDataSource.paginator = this.paginator;
		this.modalTitle= "Show Users In Location";
		this.searchForManager = "";
		this.roleSelectionManager = "all";
		// update managersList
		this.managersList = [];
		for (let mgrId of this.levelManagers) {
			const mgrData = (this.tempManagers ?? []).find((user: any) => {
				return user && user.id === mgrId;
			});

			// Skip ids we have no details for instead of pushing undefined into the table.
			if (mgrData) {
				this.managersList.push(mgrData);
			}
		}

		this.managersList.sort((mgrA: any, mgrB: any) => {
			return mgrA.name < mgrB.name ? -1 : 1;
		});
		// console.log(this.managersList);
		this.managersListSource = new MatTableDataSource<any>(this.managersList);
		// this.updateManagersTable();
	}

	getUserLevels(userDetails: any) {
		let displayValues: any = [];
		// let parents = [];

		for (let i = 0; i < userDetails.level.length; i += 1) {
			const val = userDetails.level[i];

			for (let j = 0; j < this.organisationHierarchy.length; j += 1) {
				let hierarchyLevelVals = this.organisationHierarchy[j].values;

				for (let hVal of hierarchyLevelVals) {
					if (hVal.value === val.value) {
						// parents.push(val);
						displayValues.push(hVal.displayValue);
					}
				}
			}
		}

		return new Set(displayValues);
	}

	capitalizeFirstWord(word: string) {
		return word.charAt(0).toUpperCase() + word.slice(1);
	}

	isUserManager(userId: string) {
		return this.levelManagers.includes(userId);
	}

	addToManagersList(user: any) {
		const userId = user.id;
		this.tempManagers.push(user);

		if (this.levelManagers.includes(userId)) {
			const itemIndex = this.levelManagers.findIndex((item: any) => item === userId);
			this.levelManagers.splice(itemIndex, 1);
		} else {
			this.levelManagers.push(userId);
		}

		// console.log(this.levelManagers);
	}

	async confirmManager() {
		const previousManagers = this.currentLevel?.managers ?? [];

		this.organisationHierarchy[this.levelIndex].values[this.globalIndex].managers = this.levelManagers;
		await this.spinner.show("blockLoader");
		const res: any = await this.organizationService.addManagers(this.organisationId, this.organisationHierarchy);
		await this.spinner.hide("blockLoader");

		if (!res || res.status !== 'success') {
			// Roll back so the UI keeps matching the database.
			this.organisationHierarchy[this.levelIndex].values[this.globalIndex].managers = previousManagers;
			this.levelManagers = [...previousManagers];
			this.dialog.open(SuccessComponent, {
				data: { title: "Failed", subTitle: "Unable to save the managers. Please try again." }
			});
			return;
		}

		this.snackbar.open("Managers edited successfully", "OK", {
			duration: 3000
		});
		this.managersCopy = [...this.levelManagers];
		await this.reloadManagersList();
	}

	/** Re-fetches manager details from Firestore so the table matches what was saved. */
	async reloadManagersList() {
		try {
			const res: any = await this.usersService.fetchMgrDetails(this.levelManagers);
			this.managersList = res ?? [];

			for (let mgr of this.managersList) {
				mgr.role = this.rolesData[mgr.roleId];
			}

			this.managersList.sort((mgrA: any, mgrB: any) => {
				return mgrA.name < mgrB.name ? -1 : 1;
			});

			this.tempManagers = [...this.managersList];
			if (this.data?.hierarchyOption) {
				this.data.hierarchyOption.managerNames = [...this.managersList];
			}

			this.managersListSource = new MatTableDataSource<any>(this.managersList);
		} catch (err) {
			console.log('Unable to reload managers list: ', err);
		}
	}

	getLocation() {
		let location = "";
		for (let level of this.currentLevel.levels) {
			location += `${this.capitalizeFirstWord(level)}/`;
		}

		location += this.capitalizeFirstWord(this.currentLevel.value);

		return location;
	}

	applyFilter(event: Event) {
		const filterValue = (event.target as HTMLInputElement).value;
		this.usersDetailsDataSource.filter = filterValue.trim().toLowerCase();

		if (this.usersDetailsDataSource.paginator) {
			this.usersDetailsDataSource.paginator.firstPage();
		}
	}

	filterAllUsers(role: any) {
		this.roleSelectionManager = role.id;
		// console.log(this.roleSelectionManager);

		if (this.roleSelectionManager === "all") {
			// this.usersDetailsDataSource = new MatTableDataSource<any>(this.orgAllUsers);
			this.usersDetailsDataSource.paginator = this.paginator;
		} else {
			// const usersByRole = this.orgAllUsers.filter((user: any) => user.roleId === this.roleSelectionManager);
			// // console.log(usersByRole);

			// this.usersDetailsDataSource = new MatTableDataSource<any>(usersByRole);
			// this.usersDetailsDataSource.paginator = this.paginator;
		}
	}

	getGlobalIndex() {
		let levels: any = [];
		// console.log(this.levels);
		for (let i = 0; i < this.levels.length - 1; i += 1) {
			levels.push(this.levels[i].value);
		}
		// console.log(levels);

		for (let i = 0; i < this.organisationHierarchy[this.levelIndex].values.length; i += 1) {
			let val = this.organisationHierarchy[this.levelIndex].values[i];
			// console.log(val.levels);

			if (this.sharedService.areArraysEqual(levels, val.levels) && val.value === this.levels[this.levels.length - 1].value) {
				return i;
			}
		}

		return 0;
	}

	async initializeVariables() {
		this.rolesData = this.data.rolesData;
		this.levelName = this.data.levelName;
		this.organisationId = this.data.organisationId;
		this.levels = this.data.levels;
		this.orgCode = this.data.orgCode;
		// console.log(this.levels);
		this.organisationHierarchy = this.data.organisationHierarchy;
		this.levelIndex = this.data.levelIndex;

		await this.spinner.show("blockLoader");
		await this.getUsers();
		await this.updateFilteredUsers(true);
		await this.spinner.hide("blockLoader");

		// console.log(this.organisationHierarchy);
		// console.log(this.levelIndex);
		if (this.data.dynamicGlobal) {
			this.globalIndex = this.getGlobalIndex();
		} else {
			this.globalIndex = this.data.globalIndex;
		}
		// console.log(this.globalIndex);
		this.currentLevel = this.organisationHierarchy[this.levelIndex].values[this.globalIndex];
		// Copy, never alias - `addToManagersList` mutates `levelManagers` in place and
		// a shared reference would silently edit the hierarchy before anything is saved.
		this.levelManagers = [...(this.currentLevel?.managers ?? [])];
		this.managersCopy = [...this.levelManagers];
		// this.orgAllUsers = this.data.allUsers;
		this.location = this.getLocation();

		for (let roleKey of Object.keys(this.rolesData)) {
			this.orgRolesNames.push({
				id: roleKey,
				name: this.rolesData[roleKey]
			});
		}

		// `managerNames` is only populated by the parent's hover handler, so it can be
		// missing when the icon is clicked directly. Load it from Firestore in that case.
		if (Array.isArray(this.data.hierarchyOption?.managerNames)) {
			this.tempManagers = this.data.hierarchyOption.managerNames;
			this.updateManagersTable();
		} else {
			await this.spinner.show("blockLoader");
			await this.reloadManagersList();
			await this.spinner.hide("blockLoader");
		}
	}

	async updateFilteredUsers(firstUpdate: boolean, userAdditionUpdate?: boolean) {
		if (firstUpdate) {
			this.filteredUsers = this.data.filteredUsers;
			await this.getRolesAndUpdateTable();
		} else {
			let levels: any = [];
			for (var i = 0; i < this.currentLevel.levels.length; i += 1) {
				let level = this.currentLevel.levels[i];
				levels.push({
					name: this.organisationHierarchy[i].name,
					value: level
				});
			}
			levels.push({
				name: this.organisationHierarchy[i].name,
				value: this.currentLevel.value
			});

			if (userAdditionUpdate) {
				const orgLengthFilteredUsers = this.filteredUsers.slice().length;

				const newFilteredUsers: any = await this.createOrganisationHierarchyService.getUsersAtLevel(levels, this.organisationId);

				if (newFilteredUsers.length === orgLengthFilteredUsers) {
					setTimeout(async () => {
						await this.updateFilteredUsers(false, true);
					}, 500);
				} else {
					this.filteredUsers = newFilteredUsers;
					await this.getRolesAndUpdateTable();
				}
			} else {
				this.filteredUsers = await this.createOrganisationHierarchyService.getUsersAtLevel(levels, this.organisationId);
				await this.getRolesAndUpdateTable();
			}
		}
	}

	async getRolesAndUpdateTable() {
		if (this.filteredUsers.length >= 1) {
			await this.spinner.show("blockLoader");
			for (let u of this.filteredUsers) {
				u.role = await this.getRoleName(u.roleId);
			}
			// console.log('user',user);
			await this.spinner.hide("blockLoader");
		}

		this.usersDetailsDataSource = new MatTableDataSource<any>(this.filteredUsers);
		this.usersDetailsDataSource.paginator = this.paginator;
	}

	updateManagersTable() {
		// Copy rather than alias the parent's array, and drop anyone who is no longer
		// in this location's `managers` list so the table can never show a stale row.
		this.managersList = (this.data.hierarchyOption?.managerNames ?? [])
			.filter((mgr: any) => mgr && this.levelManagers.includes(mgr.id));

		for (let mgr of this.managersList) {
			mgr.role = this.rolesData[mgr.roleId];
		}

		this.managersList.sort((mgrA: any, mgrB: any) => {
			return mgrA.name < mgrB.name ? -1 : 1;
		});

		// Always refresh the source - previously an empty list left the old rows on screen.
		this.managersListSource = new MatTableDataSource<any>(this.managersList);
	}

	async getUsers() {
		const result = await this.usersService.getInitialUsers(this.organisationId);

		if (result.lastUserDoc) {
			let users: any = result.users;
			this.users = [];

			if (users && users.length >= 1) {
				const unformattedUsers = users;

				for (let user of unformattedUsers) {
					let userObj: any = {
						id: user.id
					};

					for (let key of Object.keys(user)) {
						userObj[key] = user[key];
					}

					this.users.push(userObj);
				}

				for (let u of this.users) {
					const roleId = String(u.roleId);
					u.role = this.rolesData[roleId];
				}

				this.lastUserDoc = result.lastUserDoc;
				this.currentIndex = [0, users.length];

				this.usersDetailsDataSource = new MatTableDataSource<any>(this.users);
				// this.usersDetailsDataSource.sort = this.sort;
				this.usersDetailsDataSource.paginator = this.paginator;
			}
		}
	}

	async nextUsers() {
		if (this.currentIndex[1] < this.users.length) {
			const newBeginningIndex = Number(this.currentIndex[0]) + 100;
			const newEndingIndex = Number(this.currentIndex[1]) + 100;
			this.currentIndex = [newBeginningIndex, newEndingIndex];

			const users = this.users.slice(this.currentIndex[0], this.currentIndex[1]);
			this.usersDetailsDataSource = new MatTableDataSource<any>(users);
			// this.usersDetailsDataSource.sort = this.sort;
			this.usersDetailsDataSource.paginator = this.paginator;
		} else {
			await this.spinner.show("blockLoader");
			const allUsers: any = await this.usersService.getMoreUsers(this.organisationId, this.lastUserDoc);
			await this.spinner.hide("blockLoader");

			if (allUsers.status === "no_more_leads") {
				this.hasMoreUsers = false;
				return;
			} else if (allUsers.status === "available") {
				this.lastUserDoc = allUsers.lastUserDoc;
				this.hasPreviousUsers = true;
				this.hasMoreUsers = true;

				const users: any = allUsers.users;
				const newBeginningIndex = Number(this.currentIndex[0]) + 100;
				const newEndingIndex = Number(this.currentIndex[1]) + users.length;
				this.currentIndex = [newBeginningIndex, newEndingIndex];

				if (users && users.length >= 1) {
					const unformattedUsers = users;

					for (let user of unformattedUsers) {
						let userObj: any = {
							id: user.id,
						};

						for (let key of Object.keys(user)) {
							userObj[key] = user[key];
						}

						this.users.push(userObj);
					}

					for (let u of this.users) {
						const roleId = String(u.roleId);
						u.role = this.rolesData[roleId];
					}

					this.usersDetailsDataSource = new MatTableDataSource<any>(users);
					// this.usersDetailsDataSource.sort = this.sort;
					this.usersDetailsDataSource.paginator = this.paginator;
				}
			}
		}
	}

	async previousUsers() {
		if (this.currentIndex[0] === 0) {
			this.hasPreviousUsers = false;
			return;
		}

		this.hasMoreUsers = true;
		const newBeginningIndex = Number(this.currentIndex[0]) - 100;
		const newEndingIndex = Number(this.currentIndex[1]) - 100;
		this.currentIndex = [newBeginningIndex, newEndingIndex];
		const users = this.users.slice(this.currentIndex[0], this.currentIndex[1]);
		this.usersDetailsDataSource = new MatTableDataSource<any>(users);
		// this.usersDetailsDataSource.sort = this.sort;
		this.usersDetailsDataSource.paginator = this.paginator;
	}

	async searchUsersList() {
		if (this.searchForManager.trim().length >= 3) {
			this.isSearchingUser = true;
			const searchRes: any = await this.usersService.searchUsersTypesense(this.searchForManager, this.organisationId);
			this.isSearchingUser = false;

			for (let u of searchRes) {
				const roleId = String(u.roleId);
				u.role = this.rolesData[roleId];
			}

			this.usersDetailsDataSource = new MatTableDataSource<any>(searchRes);
			// this.usersDetailsDataSource.sort = this.sort;
			this.usersDetailsDataSource.paginator = this.paginator;
		} else {
			this.usersDetailsDataSource = new MatTableDataSource<any>(this.users);
			// this.usersDetailsDataSource.sort = this.sort;
			this.usersDetailsDataSource.paginator = this.paginator;
		}
	}

	async refreshUsers(type: string) {
		this.searchForManager = '';
		this.isSearchingUser = false;

		if (type === 'latest' || type === 'add') {
			await this.spinner.show("blockLoader");
			const result = await this.usersService.getInitialUsers(this.organisationId);
			await this.spinner.hide("blockLoader");

			if (result.lastUserDoc) {
				const users = result.users;

				for (let user of users) {
					const userSearch = this.users.findIndex((searchUser: any) => searchUser.id === user.id);
					if (userSearch === -1) {
						this.users.unshift(user);
					}
				}

				for (let u of this.users) {
					const roleId = String(u.roleId);
					u.role = this.rolesData[roleId];
				}

				this.currentIndex = [0, users.length];
				let usersToShow = this.users.slice(0, users.length);
				this.usersDetailsDataSource = new MatTableDataSource<any>(usersToShow);
				// this.usersDetailsDataSource.sort = this.sort;
				this.usersDetailsDataSource.paginator = this.paginator;
			}
		} else if (type === 'delete' || type === 'edit') {
			let slicedUsers = this.users.slice(this.currentIndex[0], this.currentIndex[1]);
			this.usersDetailsDataSource = new MatTableDataSource<any>(slicedUsers);
			// this.usersDetailsDataSource.sort = this.sort;
			this.usersDetailsDataSource.paginator = this.paginator;
		}
	}
}
